import { ChangeEvent, useEffect, useState } from "react";
import LayersIcon from "@mui/icons-material/Layers";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  DialogContentText,
  FormControlLabel,
  Switch,
  TextField,
  Tooltip
} from "@mui/material";
import { useFormikContext } from "formik";
import type { RuntimeConfig } from "../pages/ConfigPage";
import OfflineMapCfgDlg from "./OfflineMapCfgDlg";
import { useAuth } from "../context/AuthContext";

const offlineMapLicenseMessage = "Offline maps require the OfflineMap license option.";

const MapForm = () => {
  const { authorizedFetch } = useAuth();
  const formik = useFormikContext<RuntimeConfig>();
  const [tileLayersDialogOpen, setTileLayersDialogOpen] = useState(false);
  const [tileLayersDraft, setTileLayersDraft] = useState("");
  const [tileLayersInitialDraft, setTileLayersInitialDraft] = useState("");
  const [tileLayersValid, setTileLayersValid] = useState(true);
  const [offlineDialogOpen, setOfflineDialogOpen] = useState(false);
  const [restoringDefaults, setRestoringDefaults] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [offlineLicenseWarningOpen, setOfflineLicenseWarningOpen] = useState(false);
  const offlineLicensed = Boolean(formik.values.map.offlineLicensed);
  const offlineToggleDirty = formik.values.map.enableOffline !== formik.initialValues.map.enableOffline;
  const offlineDownloadsEnabled = formik.values.map.enableOffline && offlineLicensed && !offlineToggleDirty;

  useEffect(() => {
    if (offlineLicensed) {
      setOfflineLicenseWarningOpen(false);
    }
  }, [offlineLicensed]);

  const offlineToggleTooltip = offlineLicensed
    ? "Allow map tiles to be cached for offline use"
    : offlineMapLicenseMessage;
  const offlineConfigureTooltip = offlineLicensed
    ? offlineToggleDirty
      ? "Save configuration to configure offline maps"
      : "Configure offline map"
    : offlineMapLicenseMessage;

  const prettyPrintTileLayers = (value: string) => {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return JSON.stringify(parsed, null, 2);
      }
    } catch {
      // Ignore parse errors and fall back to the original value.
    }
    return value;
  };

  const handleTileLayersToggle = () => {
    const prettyValue = prettyPrintTileLayers(formik.values.map.tileLayers);
    setTileLayersInitialDraft(prettyValue);
    setTileLayersDraft(prettyValue);
    try {
      const parsed = JSON.parse(prettyValue);
      setTileLayersValid(Array.isArray(parsed));
    } catch {
      setTileLayersValid(false);
    }
    setTileLayersDialogOpen(true);
  };

  const handleTileLayersDraftChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.target;
    setTileLayersDraft(value);
    try {
      const parsed = JSON.parse(value);
      setTileLayersValid(Array.isArray(parsed));
    } catch {
      setTileLayersValid(false);
    }
  };

  const handleTileLayersCancel = () => {
    setTileLayersDraft(tileLayersInitialDraft);
    setTileLayersValid(true);
    setTileLayersDialogOpen(false);
  };

  const handleTileLayersApply = () => {
    try {
      const parsed = JSON.parse(tileLayersDraft);
      if (!Array.isArray(parsed)) {
        setTileLayersValid(false);
        return;
      }
      const formatted = JSON.stringify(parsed, null, 2);
      void formik.setFieldValue("map.tileLayers", formatted, true);
      formik.setFieldTouched("map.tileLayers", true, false);
      void formik.validateField("map.tileLayers");
      setTileLayersInitialDraft(formatted);
      setTileLayersDraft(formatted);
      setTileLayersValid(true);
      setTileLayersDialogOpen(false);
    } catch {
      setTileLayersValid(false);
    }
  };

  const handleRestoreConfirmOpen = () => {
    setRestoreError(null);
    setRestoreConfirmOpen(true);
  };

  const handleRestoreCancel = () => {
    if (restoringDefaults) {
      return;
    }
    setRestoreConfirmOpen(false);
  };

  const handleRestoreDefaults = async () => {
    setRestoringDefaults(true);
    setRestoreError(null);
    try {
      const response = await authorizedFetch("/api/config/map/default-tile-layers");
      if (!response.ok) {
        throw new Error(`Server responded ${response.status}`);
      }
      const payload: { tileLayers?: string } = await response.json();
      if (typeof payload.tileLayers !== "string" || payload.tileLayers.trim().length === 0) {
        throw new Error("Missing default tile layers");
      }
      const formatted = prettyPrintTileLayers(payload.tileLayers);
      setTileLayersDraft(formatted);
      setTileLayersValid(true);
      setRestoreConfirmOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setRestoreError(message);
    } finally {
      setRestoringDefaults(false);
    }
  };

  const tileLayersTouched = formik.touched.map?.tileLayers;
  const tileLayersFormikError = tileLayersTouched ? formik.errors.map?.tileLayers : undefined;

  const tileLayersOkEnabled = tileLayersValid && tileLayersDraft !== tileLayersInitialDraft;
  const handleOfflineToggle = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked && !offlineLicensed) {
      setOfflineLicenseWarningOpen(true);
      void formik.setFieldValue("map.enableOffline", false, false);
      return;
    }
    setOfflineLicenseWarningOpen(false);
    void formik.setFieldValue("map.enableOffline", checked);
  };
  const handleOfflineDialogOpen = () => {
    if (offlineDownloadsEnabled) {
      setOfflineDialogOpen(true);
    }
  };
  const handleOfflineDialogClose = () => setOfflineDialogOpen(false);
  const handleOfflineDialogSave = () => {
    setOfflineDialogOpen(false);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Configure base tile layers using a JSON array">
          <Button
            variant="outlined"
            startIcon={<LayersIcon fontSize="small" />}
            onClick={handleTileLayersToggle}
          >
            Tile layers
          </Button>
        </Tooltip>
      </Box>
      <Dialog
        open={tileLayersDialogOpen}
        onClose={handleTileLayersCancel}
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        <DialogTitle>Tile layers</DialogTitle>
        <DialogContent>
          <TextField
            label="Tile layers"
            value={tileLayersDraft}
            onChange={handleTileLayersDraftChange}
            multiline
            minRows={8}
            error={!tileLayersValid || Boolean(tileLayersFormikError)}
            helperText={!tileLayersValid ? "Provide a valid JSON array" : tileLayersFormikError ?? " "}
            slotProps={{ input: { sx: { fontFamily: "monospace", fontSize: "0.65rem" } } }}
            sx={{ width: { xs: "100%", sm: "60ch" }, mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", alignItems: "center", px: 3 }}>
          <Tooltip title="Restore default tile layers">
            <span>
              <IconButton aria-label="Restore default tile layers" onClick={handleRestoreConfirmOpen} size="small">
                <RestoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={handleTileLayersCancel}>Cancel</Button>
            <Button variant="contained" onClick={handleTileLayersApply} disabled={!tileLayersOkEnabled}>
              Ok
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog open={restoreConfirmOpen} onClose={handleRestoreCancel}>
        <DialogTitle>Restore tile layers</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <DialogContentText>
            Current tile layers will be replaced with the built-in defaults. Continue?
          </DialogContentText>
          {restoreError && <Alert severity="error">{restoreError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestoreCancel} disabled={restoringDefaults}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRestoreDefaults} disabled={restoringDefaults}>
            Restore
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title={offlineToggleTooltip}>
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.map.enableOffline}
                onChange={handleOfflineToggle}
                name="map.enableOffline"
              />
            }
            label="Enable offline tiles"
          />
        </Tooltip>
        <Tooltip title={offlineConfigureTooltip}>
          <span>
            <Button
              variant="outlined"
              startIcon={<MoveToInboxIcon fontSize="small" />}
              disabled={!offlineDownloadsEnabled}
              onClick={handleOfflineDialogOpen}
              size="small"
            >
              Configure
            </Button>
          </span>
        </Tooltip>
      </Box>
      {formik.values.map.enableOffline && offlineLicensed && offlineToggleDirty && (
        <Alert severity="info">
          Save configuration before downloading or managing offline maps.
        </Alert>
      )}
      <OfflineMapCfgDlg
        open={offlineDialogOpen}
        onClose={handleOfflineDialogClose}
        downloadingEnabled={offlineDownloadsEnabled}
      />
      {offlineLicenseWarningOpen && (
        <Alert severity="warning" onClose={() => setOfflineLicenseWarningOpen(false)}>
          {offlineMapLicenseMessage}
        </Alert>
      )}
    </Box>
  );
};

export default MapForm;
